/**
 * The shell's side of remote support (Fernwartung): a BLP support operator
 * asks for access, the installation's admin decides live. Master's public door
 * (backend master `CLAUDE.md`, "Remote support"; gate
 * `master.support.approve`) lists the requests and takes the decisions:
 *
 * - `GET  /v1/support/requests`            open requests and the ones of the last 30 days, `{items}`
 * - `POST /v1/support/requests/:id/approve {minutes}`
 * - `POST /v1/support/requests/:id/deny`
 * - `POST /v1/support/requests/:id/end`    ends a running session
 *
 * Public door, not the admin door: through the edge the shell reaches only the
 * public one. The routes answer a session that is no holder of the key — an
 * impersonation or a support session included — with 403 (`isNotAnApprover`);
 * userinfo's `support_approver` tells the shell beforehand.
 */
import { fetchWithAuth, getApiOrigin } from '@blpde/connect/auth';

export type SupportState = 'requested' | 'approved' | 'denied' | 'expired' | 'cancelled' | 'ended';

const STATES: readonly SupportState[] = [
  'requested',
  'approved',
  'denied',
  'expired',
  'cancelled',
  'ended',
];

/** One request as master lists it (master `support.Request`; the secret's hash is never part of it). */
export interface SupportRequest {
  id: string;
  operatorName: string;
  operatorEmail: string;
  reason: string;
  requestedMinutes: number;
  requestedAt: string;
  /** Until when the admin may decide. */
  expiresAt: string;
  state: SupportState;
  /** The admin who approved it (account id); none for the standing access. */
  approvedById?: number;
  approvedMinutes?: number;
  /** When an approved session ends (RFC 3339). */
  endsAt?: string;
  /** The link was redeemed: the session runs (or ran). */
  redeemedAt?: string;
  endedAt?: string;
  /** The standing access (Dauerfernwartung) approved it, no admin did. */
  standing: boolean;
  /** The session's account is a superadmin made for this session alone. */
  ephemeral: boolean;
  sessionAccountId?: number;
}

/** A refusal of the door: its HTTP status and master's `code` (empty when it named none). */
export class SupportApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string) {
    super(code || `support request failed (${status})`);
    this.name = 'SupportApiError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Master's public door with `/v1`: the one the shell talks to (through the edge
 * too — the admin door is not reachable from outside). The API server the
 * shell is signed in to.
 */
export function publicApiBase(): string {
  return `${getApiOrigin()}/v1`;
}

async function refusal(res: Response): Promise<SupportApiError> {
  const body = (await res.json().catch(() => ({}))) as { code?: unknown; error?: unknown };
  const code =
    typeof body.code === 'string' ? body.code : typeof body.error === 'string' ? body.error : '';
  return new SupportApiError(res.status, code);
}

/**
 * One request, no backoff of its own: the store reads the list again when it
 * has reason to (a push, the clock, its poll), and a decision is the admin's
 * click, answered at once — a refusal must not wait for retries.
 */
async function call(path: string, init: RequestInit = {}): Promise<unknown> {
  const res = await fetchWithAuth(
    `${publicApiBase()}${path}`,
    {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      },
    },
    { maxRetries: 0 }
  );
  if (!res.ok) throw await refusal(res);
  return res.json().catch(() => ({}));
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function optionalText(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined;
}

function optionalNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** A row of the list, or null when it is no request this shell knows. */
function toRequest(raw: unknown): SupportRequest | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const state = r.state as SupportState;
  if (typeof r.id !== 'string' || !r.id || !STATES.includes(state)) return null;
  return {
    id: r.id,
    operatorName: text(r.operatorName),
    operatorEmail: text(r.operatorEmail),
    reason: text(r.reason),
    requestedMinutes: optionalNumber(r.requestedMinutes) ?? 0,
    requestedAt: text(r.requestedAt),
    expiresAt: text(r.expiresAt),
    state,
    approvedById: optionalNumber(r.approvedByID),
    approvedMinutes: optionalNumber(r.approvedMinutes),
    endsAt: optionalText(r.endsAt),
    redeemedAt: optionalText(r.redeemedAt),
    endedAt: optionalText(r.endedAt),
    standing: r.standing === true,
    ephemeral: r.ephemeral === true,
    sessionAccountId: optionalNumber(r.sessionAccountID),
  };
}

/** An account the standing access may use, as master's support route lists it. */
export interface SupportAccount {
  id: number;
  name: string;
  /** Empty for an account without a domain. */
  email: string;
}

/**
 * The accounts the standing access may use (`GET /v1/support/accounts`,
 * `{items: [{id, uuid, name, email}]}`): master lists them for the holders of
 * `master.support.approve`, with or without a domain — the public account
 * directory leaves such accounts out.
 */
export async function fetchSupportAccounts(): Promise<SupportAccount[]> {
  const body = (await call('/support/accounts')) as { items?: unknown } | null;
  const rows = Array.isArray(body?.items) ? body.items : [];
  return rows.flatMap((raw): SupportAccount[] => {
    const r = raw as Record<string, unknown> | null;
    if (!r || typeof r.id !== 'number' || !Number.isFinite(r.id)) return [];
    return [{ id: r.id, name: text(r.name), email: text(r.email) }];
  });
}

/** The open and recent requests of the installation, oldest request first. */
export async function fetchSupportRequests(): Promise<SupportRequest[]> {
  const body = await call('/support/requests');
  const doc = body as { items?: unknown; requests?: unknown } | null;
  const rows = Array.isArray(body) ? body : (doc?.items ?? doc?.requests ?? []);
  if (!Array.isArray(rows)) return [];
  return rows
    .map(toRequest)
    .filter((request): request is SupportRequest => request !== null)
    .sort((a, b) => Date.parse(a.requestedAt) - Date.parse(b.requestedAt));
}

/** Approves a waiting request for `minutes` (15 to 480, at most the requested ones). */
export async function approveSupportRequest(id: string, minutes: number): Promise<void> {
  await call(`/support/requests/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
    body: JSON.stringify({ minutes }),
  });
}

/** Turns a waiting request down. */
export async function denySupportRequest(id: string): Promise<void> {
  await call(`/support/requests/${encodeURIComponent(id)}/deny`, { method: 'POST' });
}

/** Ends a running session now. */
export async function endSupportRequest(id: string): Promise<void> {
  await call(`/support/requests/${encodeURIComponent(id)}/end`, { method: 'POST' });
}

/**
 * Master has no remote support to offer: the edge link is off
 * (`503 edge_unavailable`). Nothing to show and nothing to ask again.
 */
export function isSupportUnavailable(err: unknown): boolean {
  return err instanceof SupportApiError && err.status === 503 && err.code === 'edge_unavailable';
}

/** The door says this session may not decide support requests (not a holder, or an impersonation or support session). */
export function isNotAnApprover(err: unknown): boolean {
  return err instanceof SupportApiError && (err.status === 403 || err.status === 401);
}

/** The i18n key of a refused decision, in words (409: not open any more, 410: the window is over). */
export function supportErrorKey(err: unknown): string {
  const status = err instanceof SupportApiError ? err.status : 0;
  if (status === 410) return 'connect.support.errors.expired';
  if (status === 409) return 'connect.support.errors.state';
  if (status === 403) return 'connect.support.errors.denied';
  return 'connect.support.errors.failed';
}
